/**
 * 点菜面板。
 *
 * 目标是**熟手一点就走**：所有选项都有初值（来自上次的 MealBrief），
 * 全部不改也能直接点"给我几个菜"。
 *
 * 硬约束（忌口、过敏、健康目标）刻意不放在这里：那些在设置页改。
 * 每顿饭都问一遍忌口，迟早有人漏答而把过敏原端上桌。
 */

import {
	FLAVOR_LABELS,
	FLAVOR_PREFERENCES,
	PROTEIN_LABELS,
	PROTEIN_PREFERENCES,
	storedDaysText,
	type DietProfile,
	type FridgeItem,
	type MealBrief,
} from '@fridge/shared';
import type { ReactNode } from 'react';

import { GLYPHS, IconGlyph } from './IconGlyph';
import { Pressable } from './Pressable';

const MINUTES: Array<{ label: string; value: number | null }> = [
	{ label: '15 分钟', value: 15 },
	{ label: '30 分钟', value: 30 },
	{ label: '45 分钟', value: 45 },
	{ label: '不限', value: null },
];

export interface BriefPickerProps {
	brief: MealBrief;
	onChange: (next: MealBrief) => void;
	profile: DietProfile;
	/** 冰箱里放得最久的几样，供"优先用掉"多选 */
	agedItems: FridgeItem[];
	busy: boolean;
	onGenerate: () => void;
}

export function BriefPicker({
	brief,
	onChange,
	profile,
	agedItems,
	busy,
	onGenerate,
}: BriefPickerProps): JSX.Element {
	function toggleUseUp(id: string): void {
		const has = brief.useUpItemIds.includes(id);
		onChange({
			...brief,
			useUpItemIds: has
				? brief.useUpItemIds.filter((value) => value !== id)
				: [...brief.useUpItemIds, id],
		});
	}

	return (
		<section className="surface mx-4 mb-4 p-4">
			<Field label="今天想吃什么口味">
				{FLAVOR_PREFERENCES.map((flavor) => (
					<Pressable
						key={flavor}
						scale={0.94}
						className={`chip ${brief.flavor === flavor ? 'chip-on' : ''}`}
						onClick={() => onChange({ ...brief, flavor })}
					>
						{FLAVOR_LABELS[flavor]}
					</Pressable>
				))}
			</Field>

			<Field label="荤素">
				{PROTEIN_PREFERENCES.map((protein) => (
					<Pressable
						key={protein}
						scale={0.94}
						className={`chip ${brief.protein === protein ? 'chip-on' : ''}`}
						onClick={() => onChange({ ...brief, protein })}
					>
						{PROTEIN_LABELS[protein]}
					</Pressable>
				))}
			</Field>

			<Field label="愿意花多久">
				{MINUTES.map((option) => (
					<Pressable
						key={option.label}
						scale={0.94}
						className={`chip ${brief.minutes === option.value ? 'chip-on' : ''}`}
						onClick={() => onChange({ ...brief, minutes: option.value })}
					>
						{option.label}
					</Pressable>
				))}
			</Field>

			{agedItems.length > 0 ? (
				<Field label="优先用掉（放得最久的）">
					{agedItems.map((item) => {
						const picked = brief.useUpItemIds.includes(item.id);
						return (
							<Pressable
								key={item.id}
								scale={0.94}
								className={`chip ${picked ? 'chip-on' : ''}`}
								style={picked ? undefined : { background: 'var(--warn-soft)' }}
								onClick={() => toggleUseUp(item.id)}
							>
								{item.name}
								<span className={`tnum text-[11px] ${picked ? 'opacity-70' : 'text-warn'}`}>
									{storedDaysText(item.storedAt)}
								</span>
							</Pressable>
						);
					})}
				</Field>
			) : null}

			<div className="mt-4">
				<label className="field-label" htmlFor="meal-note">
					还有别的要求吗（可留空）
				</label>
				<input
					id="meal-note"
					className="field"
					placeholder="想喝汤 / 不想开火 / 想吃上次那个"
					value={brief.note}
					onChange={(event) => onChange({ ...brief, note: event.target.value })}
				/>
			</div>

			<Pressable className="cta mt-4 w-full" disabled={busy} onClick={onGenerate}>
				<IconGlyph glyph={busy ? GLYPHS.countdown : GLYPHS.sparkle} size={17} weight="fill" />
				{busy ? '正在想菜' : '给我几个菜'}
			</Pressable>

			{/* 生成期间明说"可以走"。
			 * 请求住在应用级（不是这个页面），切页面不会丢结果；
			 * 不说的话用户会以为必须守着这一屏，那就是白等。 */}
			{busy ? (
				<p className="mt-2.5 text-center text-[11.5px] leading-relaxed text-label-3">
					你可以先去别的页面，好了会提示你，回来就能看到
				</p>
			) : null}

			<p className="mt-3 text-[11.5px] leading-relaxed text-label-2">
				忌口与健康目标在设置里，会一直生效
				{profile.avoid.length > 0 ? `：已排除 ${profile.avoid.join('、')}` : '：暂未设置忌口'}
			</p>
		</section>
	);
}

function Field({ label, children }: { label: string; children: ReactNode }): JSX.Element {
	return (
		<div className="mb-4 last:mb-0">
			<div className="field-label">{label}</div>
			<div className="-mx-1 flex gap-2 overflow-x-auto px-1 pb-0.5">{children}</div>
		</div>
	);
}
